import { ingressCopy } from '@/copy'
import type { StatusTone } from '@/ui/Status/Status'
import type {
  Delivery,
  DeliveryOutcome,
  DeliveryRoute,
} from '../../api/ingress'

interface OutcomeInfo {
  label: string
  tone: StatusTone
  /** Shown under the row when nothing reached an agent. */
  explain?: string
}

const { outcome, explain, invocation } = ingressCopy.trace

const OUTCOMES: Record<DeliveryOutcome, OutcomeInfo> = {
  routed: { label: outcome.routed, tone: 'success' },
  sunk: { label: outcome.sunk, tone: 'info', explain: explain.sunk },
  unmatched: {
    label: outcome.unmatched,
    tone: 'warning',
    explain: explain.unmatched,
  },
  dropped: {
    label: outcome.dropped,
    tone: 'warning',
    explain: explain.dropped,
  },
  partial: { label: outcome.partial, tone: 'danger', explain: explain.partial },
  failed: { label: outcome.failed, tone: 'danger' },
}

export function outcomeInfo(
  d: Pick<Delivery, 'outcome' | 'state'> & { drop_rule?: number | null },
): OutcomeInfo {
  if (d.outcome === 'dropped' && d.drop_rule != null) {
    return {
      ...OUTCOMES.dropped,
      explain: explain.droppedByRule(d.drop_rule + 1),
    }
  }
  if (d.outcome) return OUTCOMES[d.outcome]
  return {
    label: d.state === 'pending' ? outcome.pending : outcome.unknown,
    tone: 'neutral',
  }
}

/** Plain-words result of one route's emit, and whether an agent ran. */
export function invocationText(r: DeliveryRoute): {
  text: string
  tone: StatusTone
} {
  if (r.state === 'failed') return { text: invocation.failed, tone: 'danger' }
  switch (r.invocation) {
    case 'invoked': {
      const n = r.delivered
      if (n == null) return { text: invocation.invokedAgents, tone: 'success' }
      return {
        text: invocation.invoked(n),
        tone: n > 0 ? 'success' : 'warning',
      }
    }
    case 'coalesced':
      return { text: invocation.coalesced, tone: 'info' }
    case 'capped':
      return { text: invocation.capped, tone: 'warning' }
    case 'no_subscribers':
      return { text: invocation.noSubscribers, tone: 'warning' }
    case 'thread_resolved':
      return { text: invocation.threadResolved, tone: 'neutral' }
    case 'already_emitted':
      return { text: invocation.alreadyEmitted, tone: 'neutral' }
    case 'thread_closed':
      return { text: invocation.threadClosed, tone: 'neutral' }
    default:
      return r.state === 'pending'
        ? { text: invocation.pending, tone: 'neutral' }
        : { text: invocation.emitted, tone: 'neutral' }
  }
}
